// Canonical jam state (v3) — the shape the JamRoom DO owns and every client
// replicates. See docs/project/do-authoritative-sync.md.
//
// The three invariants this model exists to enforce:
//
//   1. IDENTITY IS AN ID. Never a name, never a position. Names drift (Live
//      renumbers "2-Kick"→"3-Kick"), order drifts permanently (roster reconcile
//      appends and never reorders), and both were load-bearing in the 2026-08-05
//      SweatClap lane destruction. Ords are display order only — never identity.
//
//   2. ABSENCE IS INEXPRESSIBLE. There is no "here is my whole clip list"
//      operation. Deletion is an explicit op naming an id, so a client that
//      merely FAILED to build a clip cannot say the clip is gone. That failure
//      mode is the 2026-08-13 kick wipe, the 2026-08-05 unsourceable-clip
//      divergence, and the correction-amplification family — all of them.
//
//   3. EVERY MUTATION CARRIES A REV. A client proposes against the rev it last
//      saw; a stale proposal is rejected rather than applied, so a peer working
//      from an old view can never revert newer work.
//
// Pure data + pure functions: no `node:` imports (the Worker has no
// `nodejs_compat`), no SDK, no I/O. Both runtimes and jam-sim run this file.

import { CENTER_PAN, UNITY_VOLUME, clampPan, clampVolume } from "../mixer.js";
import { isInstrumentId, type InstrumentId } from "../instruments.js";
import {
  compareClipPlace,
  type CanonicalClip,
  type CanonicalSet,
  type CanonicalTrack,
  type TrackKind,
} from "./canonical.js";
import type { ClipData } from "./protocol.js";

export type { TrackKind };

/** Track scalars that travel as field ops. Display order is NOT one of
 * them — it travels as its own op (`trackOrderSet`, below): a field an old
 * receiver doesn't know falls into its boolean branch and lands as a solo
 * write, whereas an op KIND it doesn't know is skipped by every switch. */
export type TrackField = "name" | "mute" | "solo" | "arm" | "volume" | "pan";

/** A field op's payload. `volume` and `pan` are the numeric fields — the
 * fold switches on the field NAME, never on the value's runtime type. */
export type TrackFieldValue = string | boolean | number;

export interface CanonicalTrackState {
  id: string;
  /** Display order. NOT identity — see invariant 1. */
  ord: number;
  name: string;
  mute: boolean;
  solo: boolean;
  arm: boolean;
  /**
   * Mixer fader position, 0..1 (unity ≈0.85 = 0 dB — see shared/mixer.ts).
   * Synced like mute/solo, but EXCLUDED from every digest surface
   * (canonical.ts, lane-digests.ts): the extension's background poller cannot
   * read the mixer (an in-flight async getValue() racing a handle
   * invalidation crashes the host), so a snapshot's volume is not comparable
   * and digesting it would read as permanent divergence — the `arm` lesson.
   * Detection runs on the client's dedicated gated mixer poll instead.
   */
  volume: number;
  /** Mixer pan position, -1..1 (0 = center — see shared/mixer.ts). Same
   * story as volume: synced, but absent from every digest surface (the
   * poller can't read the mixer); detection is the gated mixer poll. */
  pan: number;
  rev: number;
  /** Absent ⇒ "audio" (every lane that predates MIDI sync). Set once at
   * creation; a lane never changes kind. */
  trackKind?: TrackKind;
  /** A MIDI lane's instrument (shared/instruments.ts) — the browser synth
   * preset it plays. Absent ⇒ "sine". The room's own field, never a device:
   * Live ignores it. Its own op kind (`trackInstrumentSet`), absent from
   * every digest surface (the Set can't report it). */
  instrument?: InstrumentId;
  /**
   * Send levels to the Set's return tracks, by return index (fader
   * positions 0..1, like `volume`). Absent ⇒ none known. Synced by its own
   * op kind (`trackSendsSet`), never a field; absent from every digest
   * surface for the mixer's reason (the poller can't read a mixer).
   */
  sends?: number[];
  /**
   * The lane's device chain (2026-09-08): each device's name and its
   * parameters (name, value, range), in chain order. Absent ⇒ unknown. The
   * chain's shape is the Set's — the SDK inserts only Live's built-in
   * devices, so a room describes what a Set holds and never invents a
   * device; a browser turns knobs (`deviceParamSet`, one parameter) and a
   * receiver writes values onto a device it matches by index and name.
   * Both are their own op kinds, never fields; absent from every digest
   * surface (parameters change under a musician's hand all the time).
   */
  devices?: DeviceState[];
  /**
   * The chain's own rev (2026-09-08, slice 38): `trackDevicesSet` and
   * `deviceParamSet` cite and bump THIS, never the lane's `rev`, so a knob
   * under automation moving every poll never stales a fader, send or name
   * edit on the same lane. Absent ⇒ 0 (rooms and lanes from before it).
   */
  devicesRev?: number;
}

/** One device parameter as the Set reports it. `value` is the raw
 * DeviceParameter value in `min..max`; `quantized` parameters step. */
export interface DeviceParam {
  name: string;
  value: number;
  min: number;
  max: number;
  quantized?: boolean;
  /** A quantized parameter's value names, by step from `min` (Live's
   * `valueItems` — "Lowpass", "Highpass"…). Absent when it has none. */
  items?: string[];
}

/** Bound on a quantized parameter's value names. */
export const MAX_DEVICE_PARAM_ITEMS = 256;

/** One device in a lane's chain. `truncated`: the wire's per-lane bound cut
 * its parameters away — not a placeholder, and not a device to reshape. */
export interface DeviceState {
  name: string;
  params: DeviceParam[];
  truncated?: boolean;
}

/**
 * A device chain that is not a lane's (slice 39): the master track's
 * ("master") or a return track's ("return:<index>"). Each under its own
 * rev, exactly like a lane's `devices`/`devicesRev`.
 */
export type ChainOwner = "master" | `return:${number}`;
export interface OwnedChain {
  devices: DeviceState[];
  rev: number;
}
export const CHAIN_OWNER_RE = /^(master|return:(0|[1-9]\d?))$/;
/** A chain owner on the wire: the master, or a return the room may describe. */
export function isChainOwner(s: string): s is ChainOwner {
  if (s === "master") return true;
  const m = /^return:(0|[1-9]\d?)$/.exec(s);
  return m !== null && Number(m[1]) < MAX_RETURNS;
}

/** Bounds for a chain on the wire — one lane's chain is one DO value and
 * one broadcast frame, so the whole thing stays well under a megabyte. */
export const MAX_DEVICES = 64;
export const MAX_DEVICE_PARAMS = 512;
/** Parameters across a lane's whole chain. */
export const MAX_DEVICE_PARAMS_PER_LANE = 2048;
export const MAX_DEVICE_NAME_LENGTH = 64;

/** A return track as the Set has it — the SDK creates none, so the room
 * describes the ones the Set holds, by index. */
export interface ReturnTrack {
  name: string;
  mute: boolean;
  solo: boolean;
  volume: number;
  pan: number;
}

/** Return tracks a room may describe, and sends a lane may carry. */
export const MAX_RETURNS = 12;

export interface CanonicalClipState extends ClipData {
  id: string;
  trackId: string;
  /** Content-addressed R2 object key. The room NEVER stores a signed URL —
   * those expire, and a stored-and-served expired URL is what made every clip
   * create fail on 2026-08-13. URLs are minted at send time from this key. */
  audioKey: string | null;
  rev: number;
  /**
   * An unwarped clip a tempo rise cut at the next clip: the length it would have,
   * at the room's tempo, if nothing were in its way. Live keeps this too, and
   * gives it back when the tempo comes down. Absent when it isn't cut. An edit
   * of the clip drops it. Never on a digest surface.
   */
  fullLength?: number;
}

export interface CanonicalRoom {
  /** Monotonic room revision — bumped once per accepted batch of ops. */
  roomRev: number;
  tempo: number;
  tempoRev: number;
  tracks: CanonicalTrackState[];
  clips: CanonicalClipState[];
  /**
   * Session View scene names by row (2026-09-08) — room-level state with its
   * own rev, like tempo. Optional on the type so rooms built before it (and
   * every hand-made test room) read as "no names, rev 0"; `emptyRoom` sets
   * both. Never on a digest surface: a receiver's scenes are grown to fit
   * slots and renamed best-effort, never compared.
   */
  sceneNames?: SceneName[];
  scenesRev?: number;
  /**
   * Locators (Live's cue points), 2026-09-08 — room-level state under its
   * own rev, the whole list at once like scene names. Optional so older
   * rooms read as "none, rev 0". Never on a digest surface. A receiver
   * reconciles Live's cue points to the list only once it has been
   * asserted (rev > 0): a rev-0 list means nobody has said anything yet.
   */
  cuePoints?: CuePoint[];
  cuePointsRev?: number;
  /**
   * The Set's master fader and pan (2026-09-08) — `song.mainTrack.mixer`,
   * under its own rev like tempo. Fader POSITION 0..1 (0.85 = unity) and
   * pan -1..1, the same units as a lane's. Optional so older rooms read as
   * unity, rev 0 — "nobody asserted"; a receiver applies it only once
   * asserted. Never on a digest surface.
   */
  master?: MasterLevel;
  masterRev?: number;
  /** The Set's return tracks, by index, under their own rev (2026-09-08).
   * Optional so older rooms read as none, rev 0. */
  returns?: ReturnTrack[];
  returnsRev?: number;
  /**
   * Device chains that are not a lane's (slice 39), keyed by owner —
   * "master" or "return:<i>" — each under its own rev. Optional so older
   * rooms read as none; never on a digest surface.
   */
  chains?: Record<string, OwnedChain>;
  /**
   * The Set's scale (slice 44): `song.rootNote` / `scaleName` / `scaleMode`
   * / `scaleIntervals`, under its own rev. READ-ONLY in the SDK, so this
   * travels one way — a Set proposes its own when the musician changes it,
   * a browser follows; no receiver applies it. Optional so older rooms read
   * as unknown, rev 0. Never on a digest surface.
   */
  scale?: ScaleState;
  scaleRev?: number;
}

export interface MasterLevel {
  volume: number;
  pan: number;
}

/** A locator: a name at an arrangement beat. */
export interface CuePoint {
  name: string;
  time: number;
}

/** Locators a room may hold, and a name's length cap on the wire. */
export const MAX_CUE_POINTS = 256;
export const MAX_CUE_NAME_LENGTH = 256;

/**
 * A scene row's name on the wire: a string is ASSERTED (`""` included — a
 * cleared name, written to a Set), `null` is UNKNOWN (a row padded by a
 * browser or hydrated from a seed that had no name for it; never written,
 * so a Set's own names survive). Readers show `null` as no name.
 */
export type SceneName = string | null;

/** The Set's scale as Live's Song exposes it. */
export interface ScaleState {
  /** 0..11, C = 0. */
  rootNote: number;
  /** Live's scale name ("Major", "Minor", "Dorian", …). */
  name: string;
  /** Live 12's Scale Mode switch — the scale is in effect. */
  mode: boolean;
  /** Semitone degrees from the root, 0..11, ascending and unique. */
  intervals: number[];
}
export const MAX_SCALE_NAME_LENGTH = 64;

/** A scale on the wire, validated and cleaned, or null when malformed. */
export function validScale(v: unknown): ScaleState | null {
  if (typeof v !== "object" || v === null) return null;
  const s = v as { rootNote?: unknown; name?: unknown; mode?: unknown; intervals?: unknown };
  if (typeof s.rootNote !== "number" || !Number.isInteger(s.rootNote) || s.rootNote < 0 || s.rootNote > 11) return null;
  if (typeof s.name !== "string" || s.name.length > MAX_SCALE_NAME_LENGTH) return null;
  if (typeof s.mode !== "boolean") return null;
  if (!Array.isArray(s.intervals) || s.intervals.length > 12) return null;
  const intervals: number[] = [];
  for (const i of s.intervals) {
    if (typeof i !== "number" || !Number.isInteger(i) || i < 0 || i > 11) return null;
    if (!intervals.includes(i)) intervals.push(i);
  }
  intervals.sort((a, b) => a - b);
  return { rootNote: s.rootNote, name: s.name, mode: s.mode, intervals };
}

export function sameScale(a: ScaleState | undefined, b: ScaleState | undefined): boolean {
  if (!a || !b) return a === b;
  return a.rootNote === b.rootNote && a.name === b.name && a.mode === b.mode && a.intervals.length === b.intervals.length && a.intervals.every((x, i) => x === b.intervals[i]);
}

/** Scenes a room may name — one past the receiver's highest usable slot. */
export const MAX_SCENES = 1025;
/** A scene name's length cap on the wire. */
export const MAX_SCENE_NAME_LENGTH = 256;

export function emptyRoom(tempo = 120): CanonicalRoom {
  return {
    roomRev: 0,
    tempo,
    tempoRev: 0,
    tracks: [],
    clips: [],
    sceneNames: [],
    scenesRev: 0,
    cuePoints: [],
    cuePointsRev: 0,
    master: { volume: UNITY_VOLUME, pan: CENTER_PAN },
    masterRev: 0,
    returns: [],
    returnsRev: 0,
    chains: {},
    scaleRev: 0,
  };
}

/**
 * Deep-clone a room WITHOUT structuredClone — the Ableton host runtime doesn't
 * have it. CanonicalRoom is plain JSON-safe data, so a JSON round-trip is
 * exact, and cheap at jam scale. Used by the client projection and by the
 * merge engine's gesture-group trials.
 */
export function cloneRoom(room: CanonicalRoom): CanonicalRoom {
  return JSON.parse(JSON.stringify(room)) as CanonicalRoom;
}

// ---------------------------------------------------------------------------
// Accepted ops — what the room broadcasts. Already validated and revved; a
// receiver applies these unconditionally (that is what "authoritative" means).
// ---------------------------------------------------------------------------

export type AcceptedOp =
  | {
      kind: "trackCreated";
      id: string;
      ord: number;
      name: string;
      rev: number;
      tempId?: string;
      /** Absent ⇒ "audio" — a receiver builds an audio lane exactly as before. */
      trackKind?: TrackKind;
      /** MIDI lanes only; absent ⇒ "sine". */
      instrument?: InstrumentId;
    }
  | { kind: "trackFieldSet"; id: string; field: TrackField; value: TrackFieldValue; rev: number }
  /** Display order (2026-09-08): a browser editor reorders lanes with it.
   * Its own kind, not a field — see TrackField. Peers built before it
   * skip the op (no switch in the fleet has a default branch) and merely
   * miss the rev bump, which their next proposal on the lane repairs by
   * the ordinary stale → re-read path. No digest surface reads `ord`. */
  | { kind: "trackOrderSet"; id: string; ord: number; rev: number }
  | { kind: "trackDeleted"; id: string; rev: number }
  | {
      kind: "clipUpserted";
      id: string;
      trackId: string;
      clip: ClipData;
      audioKey: string | null;
      rev: number;
      tempId?: string;
    }
  | { kind: "clipDeleted"; id: string; trackId: string; rev: number }
  | { kind: "tempoSet"; value: number; rev: number }
  /** The whole scene-name list (rows beyond it are unnamed). Peers built
   * before it skip the kind. */
  | { kind: "sceneNamesSet"; names: SceneName[]; rev: number }
  | { kind: "cuePointsSet"; points: CuePoint[]; rev: number }
  | { kind: "masterSet"; volume: number; pan: number; rev: number }
  /** The Set's scale (slice 44), whole. Peers built before it skip the kind. */
  | { kind: "scaleSet"; scale: ScaleState; rev: number }
  | { kind: "trackSendsSet"; id: string; sends: number[]; rev: number }
  /** A MIDI lane's instrument. Peers built before it skip the kind. */
  | { kind: "trackInstrumentSet"; id: string; instrument: InstrumentId; rev: number }
  | { kind: "returnsSet"; returns: ReturnTrack[]; rev: number }
  /** A lane's whole device chain (the Set's assertion, or a seed's). */
  | { kind: "trackDevicesSet"; id: string; devices: DeviceState[]; rev: number }
  /** A non-lane chain (the master's, a return's) whole, and one of its knobs. */
  | { kind: "chainSet"; owner: ChainOwner; devices: DeviceState[]; rev: number }
  | { kind: "chainParamSet"; owner: ChainOwner; device: number; param: number; value: number; deviceName?: string; paramName?: string; rev: number }
  /** One parameter value on one device of a lane's chain. The engine stamps
   * the device's and parameter's NAMES from the room's chain, so a receiver
   * whose Set's chain has moved on since (a device inserted in Live before
   * the poll caught it) skips the write instead of turning the wrong knob. */
  | { kind: "deviceParamSet"; id: string; device: number; param: number; value: number; deviceName?: string; paramName?: string; rev: number };

/** A deep copy of a chain, so a fold never aliases an op's arrays. */
export function cloneDevices(devices: readonly DeviceState[]): DeviceState[] {
  return devices.map((d) => ({
    name: d.name,
    ...(d.truncated ? { truncated: true } : {}),
    params: d.params.map((p) => ({
      name: p.name,
      value: p.value,
      min: p.min,
      max: p.max,
      ...(p.quantized ? { quantized: true } : {}),
      ...(p.items && p.items.length > 0 ? { items: [...p.items] } : {}),
    })),
  }));
}

/** Clamp a parameter value into its range (a quantized one to a step). */
export function clampParam(value: number, p: Pick<DeviceParam, "min" | "max" | "quantized">): number {
  const lo = Math.min(p.min, p.max);
  const hi = Math.max(p.min, p.max);
  const v = Math.min(hi, Math.max(lo, value));
  return p.quantized ? Math.round(v) : v;
}

/**
 * Fold accepted ops into a room, mutating it. Total and order-dependent:
 * the room assigned the order, so applying in sequence is the definition of
 * correct. Unknown ids are skipped rather than throwing — a replica that missed
 * an op heals on the next full-state read, and a throw here would strand it.
 */
export function applyAcceptedOps(room: CanonicalRoom, ops: AcceptedOp[]): void {
  for (const op of ops) {
    switch (op.kind) {
      case "trackCreated": {
        if (room.tracks.some((t) => t.id === op.id)) break; // idempotent replay
        room.tracks.push({
          id: op.id,
          ord: op.ord,
          name: op.name,
          mute: false,
          solo: false,
          arm: false,
          volume: UNITY_VOLUME,
          pan: CENTER_PAN,
          rev: op.rev,
          ...(op.trackKind === "midi" ? { trackKind: "midi" as const } : {}),
          ...(op.trackKind === "midi" && isInstrumentId(op.instrument) ? { instrument: op.instrument } : {}),
        });
        room.tracks.sort((a, b) => a.ord - b.ord);
        break;
      }
      case "trackFieldSet": {
        const t = room.tracks.find((x) => x.id === op.id);
        if (!t) break;
        // Switch on the FIELD, never on the value's runtime type — an op
        // naming a field this build doesn't know must land nowhere (and not
        // rev the track), so a newer peer's field never writes a stray
        // property or masks a real change here.
        if (op.field === "name") t.name = String(op.value);
        else if (op.field === "volume") t.volume = clampVolume(Number(op.value));
        else if (op.field === "pan") t.pan = clampPan(Number(op.value));
        else if (op.field === "mute" || op.field === "solo" || op.field === "arm") {
          t[op.field] = Boolean(op.value);
        } else break;
        t.rev = op.rev;
        break;
      }
      case "trackOrderSet": {
        const t = room.tracks.find((x) => x.id === op.id);
        if (!t) break;
        t.ord = op.ord;
        t.rev = op.rev;
        room.tracks.sort((a, b) => a.ord - b.ord);
        break;
      }
      case "trackDeleted": {
        const i = room.tracks.findIndex((x) => x.id === op.id);
        if (i < 0) break;
        room.tracks.splice(i, 1);
        // Cascade: a track's clips cannot outlive it.
        room.clips = room.clips.filter((c) => c.trackId !== op.id);
        break;
      }
      case "clipUpserted": {
        const existing = room.clips.find((c) => c.id === op.id);
        if (existing) {
          // An update that carries no `notes` keeps the room's notes (the MIDI
          // analogue of "a null audioKey keeps what the room has"). Explicit,
          // not an accident of Object.assign: an in-process op could hold the
          // key with an undefined value, and that must not wipe the notes.
          const { notes, notesDigest, ...rest } = op.clip;
          const newAudio = op.audioKey !== existing.audioKey;
          Object.assign(
            existing,
            rest,
            notes !== undefined ? { notes } : {},
            notesDigest !== undefined ? { notesDigest } : {},
            { trackId: op.trackId, audioKey: op.audioKey, rev: op.rev },
          );
          delete existing.fullLength; // edited: this is its length now
          // `slot` is a PLACE, not content: an op without one puts the clip on
          // the timeline, so a stale slot must not survive the assign (a
          // session→arrangement move would otherwise read as both at once).
          if (op.clip.slot === undefined) {
            delete existing.slot;
            delete existing.slotLooping; // a timeline clip has no launch
          }
          // Warp markers describe ONE object: new audio without them is a
          // new object with a new (unknown) map, never the old one's.
          if (op.clip.warpMarkers === undefined && newAudio) delete existing.warpMarkers;
          // Likewise a render is of one object's sound.
          if (op.clip.render === undefined && newAudio) delete existing.render;
        } else {
          room.clips.push({
            ...op.clip,
            id: op.id,
            trackId: op.trackId,
            audioKey: op.audioKey,
            rev: op.rev,
          });
        }
        break;
      }
      case "clipDeleted": {
        const i = room.clips.findIndex((c) => c.id === op.id);
        if (i >= 0) room.clips.splice(i, 1);
        break;
      }
      case "tempoSet": {
        rescaleUnwarpedClips(room, room.tempo, op.value);
        room.tempo = op.value;
        room.tempoRev = op.rev;
        break;
      }
      case "sceneNamesSet": {
        room.sceneNames = [...op.names];
        room.scenesRev = op.rev;
        break;
      }
      case "cuePointsSet": {
        room.cuePoints = op.points.map((c) => ({ name: c.name, time: c.time }));
        room.cuePointsRev = op.rev;
        break;
      }
      case "masterSet": {
        room.master = { volume: clampVolume(op.volume), pan: clampPan(op.pan) };
        room.masterRev = op.rev;
        break;
      }
      case "scaleSet": {
        const scale = validScale(op.scale);
        if (!scale) break;
        room.scale = scale;
        room.scaleRev = op.rev;
        break;
      }
      case "trackSendsSet": {
        const t = room.tracks.find((x) => x.id === op.id);
        if (!t) break;
        t.sends = op.sends.map(clampVolume);
        t.rev = op.rev;
        break;
      }
      case "trackInstrumentSet": {
        const t = room.tracks.find((x) => x.id === op.id);
        if (!t || t.trackKind !== "midi" || !isInstrumentId(op.instrument)) break;
        t.instrument = op.instrument;
        t.rev = op.rev;
        break;
      }
      case "trackDevicesSet": {
        const t = room.tracks.find((x) => x.id === op.id);
        if (!t) break;
        t.devices = cloneDevices(op.devices);
        t.devicesRev = op.rev;
        break;
      }
      case "deviceParamSet": {
        const t = room.tracks.find((x) => x.id === op.id);
        if (!t) break;
        const p = t.devices?.[op.device]?.params[op.param];
        if (!p) break;
        p.value = clampParam(op.value, p);
        t.devicesRev = op.rev;
        break;
      }
      case "chainSet": {
        room.chains ??= {};
        room.chains[op.owner] = { devices: cloneDevices(op.devices), rev: op.rev };
        break;
      }
      case "chainParamSet": {
        const chain = room.chains?.[op.owner];
        const p = chain?.devices[op.device]?.params[op.param];
        if (!chain || !p) break;
        p.value = clampParam(op.value, p);
        chain.rev = op.rev;
        break;
      }
      case "returnsSet": {
        room.returns = op.returns.map((r) => ({
          name: r.name,
          mute: r.mute,
          solo: r.solo,
          volume: clampVolume(r.volume),
          pan: clampPan(r.pan),
        }));
        room.returnsRev = op.rev;
        break;
      }
    }
  }
}

/** Clips of one track: arrangement clips in timeline order, then Session View
 * clips by slot (canonical.ts compareClipPlace). */
export function clipsOfTrack(room: CanonicalRoom, trackId: string): CanonicalClipState[] {
  return room.clips.filter((c) => c.trackId === trackId).sort(compareClipPlace);
}

/** Only the arrangement (timeline) clips of one track, in startTime order. */
/**
 * An unwarped audio clip keeps its length in SECONDS, so its length in beats
 * follows the tempo, as Live has it (emulator row 19): every copy of the room
 * moves it the same way, or each tempo change reads as a lane to repair and a
 * clip to re-render. Where the new length no longer fits, Live cuts it at the
 * next clip on the lane, and gives it back when the tempo drops (measured,
 * 2026-10-30). No rev: nobody edited the clip, and an edit in flight still cites
 * the one it had.
 */
export function rescaleUnwarpedClips(room: CanonicalRoom, from: number, to: number): void {
  if (!(from > 0) || !(to > 0) || from === to) return;
  const ratio = to / from;
  for (const c of room.clips) {
    if (c.isWarped || c.clipKind === "midi") continue;
    const full = (c.fullLength ?? c.duration) * ratio;
    let duration = full;
    if (c.slot === undefined) {
      const next = room.clips
        .filter((o) => o.trackId === c.trackId && o.slot === undefined && o.startTime > c.startTime)
        .reduce((nearest, o) => Math.min(nearest, o.startTime), Number.POSITIVE_INFINITY);
      duration = Math.min(duration, next - c.startTime);
    }
    const scale = duration / c.duration;
    c.duration = duration;
    c.startMarker *= scale;
    c.endMarker *= scale;
    c.loopStart *= scale;
    c.loopEnd *= scale;
    if (duration < full) c.fullLength = full;
    else delete c.fullLength;
  }
}

export function arrangementClipsOfTrack(room: CanonicalRoom, trackId: string): CanonicalClipState[] {
  return clipsOfTrack(room, trackId).filter((c) => c.slot === undefined);
}

/** Only the Session View clips of one track, by slot. */
export function sessionClipsOfTrack(room: CanonicalRoom, trackId: string): CanonicalClipState[] {
  return clipsOfTrack(room, trackId).filter((c) => c.slot !== undefined);
}

/**
 * Project a room onto the digest comparison surface, so v3 canonical state and
 * a client's Live snapshot stay comparable through the SAME canonicaliser that
 * v2 used. Keeping one comparison surface is what lets a client answer "am I
 * synced?" without the room having to understand Live.
 */
export function toCanonicalSet(room: CanonicalRoom): CanonicalSet {
  const lane = (t: CanonicalTrackState): CanonicalTrack => ({
    name: t.name,
    mute: t.mute,
    solo: t.solo,
    arm: t.arm,
    arrangementClips: clipsOfTrack(room, t.id).map(canonicalClipOf),
    ...(t.trackKind === "midi" ? { trackKind: "midi" as const } : {}),
  });
  const audio = room.tracks.filter((t) => t.trackKind !== "midi");
  const midi = room.tracks.filter((t) => t.trackKind === "midi");
  return {
    song: { tempo: room.tempo },
    audioTracks: audio.map(lane),
    ...(midi.length > 0 ? { midiTracks: midi.map(lane) } : {}),
  };
}

/** A room clip on the comparison surface. MIDI clips carry their digest (and
 * notes, for consumers that re-emit them); audio clips are exactly what they
 * were before MIDI sync. */
export function canonicalClipOf(c: ClipData): CanonicalClip {
  const out: CanonicalClip = {
    name: c.name,
    startTime: c.startTime,
    duration: c.duration,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    looping: c.looping,
    loopStart: c.loopStart,
    loopEnd: c.loopEnd,
    color: c.color,
    muted: c.muted,
    filePath: c.filePath,
    warping: c.isWarped,
    warpMode: { value: c.warpModeValue },
  };
  if (c.clipKind === "midi") {
    out.clipKind = "midi";
    if (c.notesDigest !== undefined) out.notesDigest = c.notesDigest;
    if (c.notes !== undefined) out.notes = c.notes;
  }
  if (c.slot !== undefined) out.slot = c.slot;
  if (c.slotLooping !== undefined) out.slotLooping = c.slotLooping;
  return out;
}
